/**
 * @author Adam Vlasek <adam.vlasek@styrax.cz>
 */

import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { ObjectShape } from 'yup/lib/object';

import useMounted from '../../../hooks/useMounted';

import yup from '../../../services/ValidationService';
import WorkerQueue from '../../../services/WorkerQueue';
import TimeoutStack from '../../../services/TimeoutStack';
import { objectValuesReplace } from '../../../services/CommonFunctions';

import StringAssociativeArrayInterface from '../../../types/StringAssociativeArrayInterface';

type ErrorType = string | undefined;
type DefaultType = StringAssociativeArrayInterface<any>;
type ChangeHandlerType = (e: React.ChangeEvent<HTMLFormElement>) => void;

export interface IFormChildrenProps<T = DefaultType> {
    values: T
    setValues: (newValues: DefaultType, setAsChanged?: boolean) => void
    changeHandler: (e: React.ChangeEvent<HTMLFormElement>) => void
    reset: () => void
    submitHandler: ChangeHandlerType
    errors: StringAssociativeArrayInterface<ErrorType>
    isValid: boolean
}

interface IField {
    children: JSX.Element
    name: string
}

interface IFormHoc<T = DefaultType> {
    children: (p: IFormChildrenProps<T>) => JSX.Element
    validationSchema?: ObjectShape
    defaultValues?: any
    onChangeTransformer?: (v: T) => T
    disabled?: boolean
    onSubmit: (v: T) => void
    onChange?: (v: T) => void
}

interface IFormPropsContext {
    values?: any
    errors?: StringAssociativeArrayInterface<ErrorType>
    changeHandler?: ChangeHandlerType
    validationSchema?: any
    disabled?: boolean
}

const FormPropsContext = createContext<IFormPropsContext>({});

/**
 * Sprava hodnot formulare, validace formulare. Policka formulare musi byt obalena elementem Field nebo FieldChecked - zajistujici napojeni policka na FormHoc.
 * @param children Funkce (p: IFormChildrenProps<T>) => JSX.Element vracejici obsah formulare.
 * @param defaultValues Objekt s defaulnima hodnotama policek formulare.
 * @param validationSchema Instance objektu yup s validacnim schematem formulare.
 * @param onSubmit Funkce (v: T) => void, jenz se zavola pri odesilani formulare (pouze pokud je validni).
 * @param onChange Funkce (v: T) => void, jenz se zavola pri zmene hodnoty policka formulare (pouze pokud je validni).
 * @param onChangeTransformer Funkce (v: T) => T, jenz se zavola pri zmene hodnoty policka formulare (pouze pokud je validni) a vraci novy hodnoty formulare (hodi se pro prepocty hodnot na zaklade zmeny nejakeho pole).
 * @example <FormHoc defaultValues={} validationSchema={} onSubmit={} onChange={}>children</FormHoc>
 * @returns React komponenta, context FormPropsContext({errors, values, changeHandler})
 */
function FormHoc<T = DefaultType>({ children, defaultValues, validationSchema, disabled, onSubmit, onChange, onChangeTransformer }: IFormHoc<T>): JSX.Element {
    const isMounted = useMounted();
    const [vals, setVals] = useState<any>(objectValuesReplace(defaultValues || {}, null, undefined));//hodnoty policek ve formulari
    const [errors, setErrors] = useState<StringAssociativeArrayInterface<ErrorType>>({});//validacni chyby ve formulari
    const [changed, setChanged] = useState<Array<string>>([]);//priznak, jestli bylo policko zmeneno nebo jestli ma originalni hodnotu
    const [isValid, setValid] = useState<boolean>(!validationSchema);//validita celeho formulare

    const values = useMemo(
        () => {
            if (!onChangeTransformer) return vals;

            return onChangeTransformer(vals);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [vals]
    );

    const safeSetState = (fce: (data: any) => any, data: any) => {
        isMounted.current && fce(data);
    };
    const timer = useMemo(() => new TimeoutStack(500), []);
    const validationWorkerQueue = useMemo(
        () => new WorkerQueue(),
        []
    );

    useEffect(
        () => onChange && onChange(values),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [values]
    );

    //validace celeho formulare (i nezmenenych policek) - je potreba pro disablovani submit tlacitka
    useEffect(
        () => {
            if (!validationSchema) return;

            validationWorkerQueue.push(() => (
                yup
                    .object()
                    .shape(validationSchema)
                    .validate(values)
            ))
                .then(() => {
                    safeSetState(setValid, true);
                    onChange && changed.length && safeSetState(onChange, values);
                })
                .catch((e) => {
                    console.error('invalid', e.message);
                    console.log(values);
                    safeSetState(setValid, false);
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [changed, values, validationSchema]
    );

    //validace vsech zmenenych policek - pouziva se k vypisovani chyby u policka
    useEffect(
        () => {
            if (!validationSchema || !changed?.length) return;

            timer.push(
                () => {
                    if (!isMounted.current) return;

                    Promise.all(
                        changed.map((name: string) => new Promise(async resolve => {
                            try {
                                await yup
                                    .object()
                                    .shape({ [name]: validationSchema[name] })
                                    .validate(values);
                                resolve({ name, error: undefined });
                            }
                            catch (e: any) {
                                resolve({ name, error: e.message });
                            }
                        }))
                    )
                        .then(res => {
                            const out: StringAssociativeArrayInterface<string | undefined> = {};
                            res.forEach((item: any) => item.error && (out[item.name] = item.error));

                            Object.keys(errors)
                                .filter(key => !changed.includes(key))
                                .forEach(key => out[key] = errors[key]);

                            safeSetState(setErrors, out);
                        });
                }
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [validationSchema, changed]
    );

    //validace vsech policek po inicializaci formulare - po inicializaci formulare vypise chyby u vsech policek
    useEffect(
        () => {
            if (!validationSchema) return;

            // timer.push(
            //     () => {
            if (!isMounted.current) return;

            Promise.all(
                Object.keys(values)
                    .filter((name: string) => values[name] !== undefined && values[name] !== null)
                    .map((name: string) => new Promise(async resolve => {
                        try {
                            await yup
                                .object()
                                .shape({ [name]: validationSchema[name] })
                                .validate(values);
                            resolve({ name, error: undefined });
                        }
                        catch (e: any) {
                            resolve({ name, error: e.message });
                        }
                    }))
            )
                .then(res => {
                    const out: StringAssociativeArrayInterface<string | undefined> = {};
                    res.forEach((item: any) => item.error && (out[item.name] = item.error));
                    safeSetState(setErrors, out);
                });
            //     }
            // );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [defaultValues]
    );

    //umozni manualni nastaveni hodnot ve formulari pro netypicke situace
    const setValues = useCallback(
        (newValues: StringAssociativeArrayInterface<any>, setAsChanged: boolean = true) => {
            const tmp = { ...values };
            const tmpChanged = new Set(changed);

            for (const key in newValues) {
                //prazdny string se nahradi za undefined - aby se na BE neposilaly prazdne stringy
                tmp[key] = newValues[key] === '' ? undefined : newValues[key];
                tmpChanged.add(key);
            }

            setVals(tmp);
            if (setAsChanged) setChanged([...tmpChanged]);
        },
        [setVals, setChanged, values, changed]
    );

    //changeHandler - pouzije se jako onChange na policko. Po zmene policka prenese hodnotu do FormHoc
    const changeHandler = useCallback(
        ({ target }: React.ChangeEvent<HTMLFormElement>) => {
            setValues({
                [target.name]: target.type === 'checkbox' ?
                    target.checked :
                    target.type === 'file' ?
                        Array.from(target.files) :
                        target.value
            });
        },
        [setValues]
    );

    //resetovani formulare na defaultni hodnoty
    const reset = useCallback(
        () => {
            setValues(defaultValues);
            setChanged([]);
        },
        [setValues, defaultValues]
    );

    //submitHandler - formular se necha odeslat pouze validni
    const submitHandler = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event?.preventDefault();
            isValid && onSubmit(values);
        },
        [values, onSubmit, isValid]
    );

    return (
        <FormPropsContext.Provider value={{ errors, values, changeHandler, validationSchema, disabled }}>
            {
                children({
                    values,
                    setValues,
                    changeHandler,
                    reset,
                    submitHandler,
                    errors,
                    isValid
                })
            }
        </ FormPropsContext.Provider>
    );
}

/**
 * Obalka napojujici formularovy prvek na FormHoc. Predava props name, value, required, onChange a error. Pro checkbox pouzivat komponentu FieldChecked!
 */
export const Field = ({ children, name }: IField) => {
    return (
        <FormPropsContext.Consumer>
            {
                ({ values, errors, changeHandler, validationSchema, disabled }: IFormPropsContext) => React.cloneElement(
                    children,
                    {
                        name,
                        required: validationSchema?.[name]?.exclusiveTests.required,
                        value: values?.[name] === undefined || values?.[name] === null ? '' : values?.[name],
                        onChange: changeHandler,
                        error: errors?.[name],
                        disabled: children.props.disabled === undefined ? disabled : children.props.disabled
                    }
                )
            }
        </FormPropsContext.Consumer>
    );
};

/**
 * Obalka napojujici checkbox na FormHoc. Predava props name, checked, required, onChange a error.
 */
export const FieldChecked = ({ children, name }: IField) => (
    <FormPropsContext.Consumer>
        {
            ({ values, errors, changeHandler, validationSchema, disabled }: IFormPropsContext) => React.cloneElement(
                children,
                {
                    name,
                    required: validationSchema?.[name]?.exclusiveTests.required,
                    checked: values?.[name] === undefined || values?.[name] === false ? '' : values?.[name],
                    onChange: changeHandler,
                    error: errors?.[name],
                    disabled: children.props.disabled === undefined ? disabled : children.props.disabled
                }
            )
        }
    </FormPropsContext.Consumer>
);

export default FormHoc;
